import { API_ENDPOINTS, httpClientInstance } from "@/api"
import httpClient from "@/api/http-client"
import { createFetchGuard, isFresh } from "@/utils/fetch-guard"
import { create } from "zustand"

// Server interface matching API response from /v1/aws-connect/instances/list/
export interface Server {
  id: number
  instance_id: string
  name: string
  owner_email: string
  instance_type_name: string
  instance_type_vcpu: number
  instance_type_memory_gb: string
  instance_type_hourly_cost: string
  status: "running" | "stopped" | "pending" | "stopping" | "starting" | "terminated"
  public_ip: string | null
  private_ip: string | null
  is_running: boolean
  is_stopped: boolean
  availability_zone: string
  aws_region: string
  monthly_budget: number | null
  created_at: string
  launch_time: string
  department: number | null
  department_name: string | null
  /** False when the instance's department is deactivated (SSH is disabled for it). Absent on older payloads. */
  department_is_active?: boolean
  allocation_id: number | null
}

// Instance type interface matching API response from /v1/aws-connect/instance-types/list/?region=<region>
// Specs and pricing are fetched live from AWS for the given region, not a static catalog.
export interface InstanceType {
  id: number
  instance_type: string
  region: string
  vcpu: number
  memory_gb: string
  hourly_cost: string
  description: string
  is_active: boolean
  created_at: string
  updated_at: string
}

// Detailed server interface matching API response from /v1/aws-connect/instances/{id}/retrieve/
export interface ServerDetail {
  id: number
  instance_id: string
  name: string
  owner: {
    id: number
    user: {
      id: number
      username: string
      email: string
      first_name: string
      last_name: string
    }
    email: string
    organization: string
    budget_threshold: string
    total_instances: number
    total_monthly_cost: number
    created_at: string
    updated_at: string
  }
  instance_type_detail: InstanceType
  status: string
  private_ip: string | null
  public_ip: string | null
  ami_id: string
  vpc_id: string
  subnet_id: string
  availability_zone: string
  tags: Record<string, string> | null
  enable_backup: boolean
  enable_ssm: boolean
  rdp_enabled: boolean
  rdp_gateway: string | null
  is_running: boolean
  is_stopped: boolean
  created_at: string
  launch_time: string
}

// CloudWatch metric datapoint
export interface MetricDatapoint {
  Timestamp: string
  Average?: number
  Sum?: number
  Maximum?: number
  Minimum?: number
  SampleCount?: number
  Unit?: string
}

// Single CloudWatch metric response
export interface MetricResult {
  Label: string
  Datapoints: MetricDatapoint[]
}

// Full metrics response from /v1/aws-connect/instances/{id}/get-metrics/
export interface InstanceMetrics {
  cpu_utilization: MetricResult
  network_in: MetricResult
  [key: string]: MetricResult
}

// Core EC2 CloudWatch metrics snapshot for one instance, as returned by
// /v1/aws-connect/instances/dashboard-metrics/ (Super Admin Dashboard widget)
export interface DashboardInstanceMetrics {
  cpu_utilization: number | null
  network_in: number | null
  network_out: number | null
  disk_read_ops: number | null
  disk_write_ops: number | null
}

export interface DashboardMetricsInstance {
  instance_id: string
  name: string
  region: string
  metrics: DashboardInstanceMetrics | null
  error: string | null
}

export interface DashboardMetricsResponse {
  instances: DashboardMetricsInstance[]
  setup_required?: boolean
}

// Check-status response types
export interface InstanceStatusCheck {
  AvailabilityZone: string
  InstanceId: string
  InstanceState: { Code: number; Name: string }
  InstanceStatus: { Details: { Name: string; Status: string }[]; Status: string }
  SystemStatus: { Details: { Name: string; Status: string }[]; Status: string }
}

export interface AwsInstanceInfo {
  instance_id: string
  instance_type: string
  state: string
  private_ip: string | null
  public_ip: string | null
  subnet_id: string
  vpc_id: string
  availability_zone: string
  ami_id: string
  launch_time: string
  security_groups: string[]
  tags: Record<string, string>
}

export interface InstanceStatusResponse {
  instance_id: string
  aws_instance: AwsInstanceInfo
  aws_status_checks: InstanceStatusCheck[]
  synced_db_instance: ServerDetail & {
    last_status_check: string | null
    terminated_at: string | null
    security_rules: unknown[]
    current_cost: number
    monthly_budget: number | null
  }
}

// AWS region as returned by /v1/aws-connect/regions/list/
export interface AwsRegion {
  code: string
  name: string
  geography: string
  opt_in_status: string
}

// Payload for creating a server
export interface CreateServerPayload {
  name: string
  instance_type: string
  ami_id: string
  region: string
}

// Payload for updating a server
export interface UpdateServerPayload {
  name: string
  instance_type_id: number
}

// Store interface
interface ManageServerStore {
  // State
  servers: Server[]
  serversLoaded: boolean
  instanceTypes: InstanceType[]
  regions: AwsRegion[]
  isLoadingRegions: boolean
  serverDetail: ServerDetail | null
  isLoading: boolean
  isLoadingInstanceTypes: boolean
  isLoadingDetail: boolean
  serverMetrics: InstanceMetrics | null
  isLoadingMetrics: boolean
  metricsError: string | null
  serverStatus: InstanceStatusResponse | null
  isLoadingStatus: boolean
  statusError: string | null
  downloadingRdpId: number | null
  error: string | null
  selectedServers: Set<number>
  dashboardMetrics: DashboardMetricsInstance[]
  dashboardMetricsSetupRequired: boolean
  isLoadingDashboardMetrics: boolean
  dashboardMetricsError: string | null
  /** Epoch ms of the last successful dashboard-metrics fetch; a failed fetch leaves it unchanged. */
  dashboardMetricsFetchedAt: number | null

  // Actions
  fetchServers: () => Promise<void>
  fetchInstanceTypes: (region: string) => Promise<void>
  fetchRegions: () => Promise<void>
  fetchServerDetail: (id: number) => Promise<void>
  clearServerDetail: () => void
  fetchServerMetrics: (id: number) => Promise<void>
  clearServerMetrics: () => void
  /** Joins a pending request and skips while fresh (5 min). */
  fetchDashboardMetrics: () => Promise<void>
  /** Drops the dashboard metrics and ignores a request still pending (sign-out, AWS connection change). */
  resetDashboardMetrics: () => void
  fetchServerStatus: (id: number) => Promise<void>
  clearServerStatus: () => void
  downloadRdpFile: (id: number, filename?: string) => Promise<void>
  /** Creates the server and returns it (its database id and EC2 instance id). */
  createServer: (payload: CreateServerPayload) => Promise<{ id: number; instance_id: string }>
  updateServer: (id: number, payload: UpdateServerPayload) => Promise<void>
  startServer: (id: number) => Promise<void>
  stopServer: (id: number) => Promise<void>
  restartServer: (id: number) => Promise<void>
  terminateServer: (id: number) => Promise<void>
  assignDepartment: (instanceId: number, departmentId: number) => Promise<void>
  unassignDepartment: (instanceId: number) => Promise<void>
  selectServer: (id: number) => void
  unselectServer: (id: number) => void
  selectAllServers: () => void
  clearSelection: () => void
  reset: () => void
}

// The dashboard widget reads CloudWatch through the backend: share one pending request and skip refetches while fresh.
const dashboardMetricsGuard = createFetchGuard()

export const useManageServerStore = create<ManageServerStore>((set, get) => ({
  // Initial state
  servers: [],
  serversLoaded: false,
  instanceTypes: [],
  regions: [],
  isLoadingRegions: false,
  serverDetail: null,
  isLoading: false,
  isLoadingInstanceTypes: false,
  isLoadingDetail: false,
  serverMetrics: null,
  isLoadingMetrics: false,
  serverStatus: null,
  isLoadingStatus: false,
  statusError: null,
  downloadingRdpId: null,
  metricsError: null,
  error: null,
  selectedServers: new Set(),
  dashboardMetrics: [],
  dashboardMetricsSetupRequired: false,
  isLoadingDashboardMetrics: false,
  dashboardMetricsError: null,
  dashboardMetricsFetchedAt: null,

  // Actions
  fetchInstanceTypes: async (region: string) => {
    set({ isLoadingInstanceTypes: true, instanceTypes: [] })
    try {
      const data = await httpClientInstance.get<InstanceType[]>(API_ENDPOINTS.awsConnect.instanceTypes.list, {
        region,
      })
      set({ instanceTypes: data, isLoadingInstanceTypes: false })
    } catch (error: any) {
      set({ isLoadingInstanceTypes: false })
      throw new Error(error.message || "Failed to fetch instance types")
    }
  },

  fetchRegions: async () => {
    set({ isLoadingRegions: true })
    try {
      const data = await httpClientInstance.get<AwsRegion[]>(API_ENDPOINTS.awsConnect.regions.list)
      set({ regions: data, isLoadingRegions: false })
    } catch (error: any) {
      set({ isLoadingRegions: false })
      throw new Error(error.message || "Failed to fetch regions")
    }
  },

  fetchServerDetail: async (id: number) => {
    set({ isLoadingDetail: true, serverDetail: null })
    try {
      const data = await httpClientInstance.get<ServerDetail>(API_ENDPOINTS.awsConnect.instances.retrieve(id))
      set({ serverDetail: data, isLoadingDetail: false })
    } catch (error: any) {
      set({ isLoadingDetail: false })
      throw new Error(error.message || "Failed to fetch server details")
    }
  },

  clearServerDetail: () => {
    set({ serverDetail: null })
  },

  fetchServerMetrics: async (id: number) => {
    set({ isLoadingMetrics: true, metricsError: null, serverMetrics: null })
    try {
      const data = await httpClientInstance.get<InstanceMetrics>(API_ENDPOINTS.awsConnect.instances.getMetrics(id))
      set({ serverMetrics: data, isLoadingMetrics: false })
    } catch (error: any) {
      const message = error.message || "Failed to fetch server metrics"
      set({ metricsError: message, isLoadingMetrics: false })
    }
  },

  clearServerMetrics: () => {
    set({ serverMetrics: null, metricsError: null })
  },

  fetchDashboardMetrics: async () => {
    if (isFresh(get().dashboardMetricsFetchedAt)) return
    await dashboardMetricsGuard.run(async (isCurrent) => {
      set({ isLoadingDashboardMetrics: true, dashboardMetricsError: null })
      try {
        const data = await httpClientInstance.get<DashboardMetricsResponse>(
          API_ENDPOINTS.awsConnect.instances.dashboardMetrics
        )
        if (isCurrent()) {
          set({
            dashboardMetrics: data.instances,
            dashboardMetricsSetupRequired: Boolean(data.setup_required),
            isLoadingDashboardMetrics: false,
            dashboardMetricsFetchedAt: Date.now(),
          })
        }
      } catch (error: unknown) {
        const message = error instanceof Error && error.message ? error.message : "Failed to fetch dashboard metrics"
        if (isCurrent()) set({ dashboardMetricsError: message, isLoadingDashboardMetrics: false })
      }
    })
  },

  resetDashboardMetrics: () => {
    dashboardMetricsGuard.reset()
    set({
      dashboardMetrics: [],
      dashboardMetricsSetupRequired: false,
      isLoadingDashboardMetrics: false,
      dashboardMetricsError: null,
      dashboardMetricsFetchedAt: null,
    })
  },

  fetchServerStatus: async (id: number) => {
    set({ isLoadingStatus: true, statusError: null, serverStatus: null })
    try {
      const data = await httpClientInstance.get<InstanceStatusResponse>(
        API_ENDPOINTS.awsConnect.instances.checkStatus(id)
      )
      set({ serverStatus: data, isLoadingStatus: false })
    } catch (error: any) {
      const message = error.message || "Failed to fetch server status"
      set({ statusError: message, isLoadingStatus: false })
    }
  },

  clearServerStatus: () => {
    set({ serverStatus: null, statusError: null })
  },

  downloadRdpFile: async (id: number, filename?: string) => {
    set({ downloadingRdpId: id })
    try {
      const response = await httpClient.get(API_ENDPOINTS.awsConnect.instances.generateRdpFile(id), {
        responseType: "blob",
      })
      const blob = new Blob([response.data], { type: "application/x-rdp" })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = filename ?? `instance-${id}.rdp`
      a.click()
      URL.revokeObjectURL(url)
    } finally {
      set({ downloadingRdpId: null })
    }
  },

  createServer: async (payload: CreateServerPayload) => {
    try {
      const created = await httpClientInstance.post<{ id: number; instance_id: string }>(
        API_ENDPOINTS.awsConnect.instances.create,
        payload
      )
      // Refresh the server list after creation
      await get().fetchServers()
      return created
    } catch (error: any) {
      const message =
        typeof error.response?.data === "object" && error.response?.data?.detail
          ? error.response.data.detail
          : error.message || "Failed to create server"
      throw new Error(message)
    }
  },

  updateServer: async (id: number, payload: UpdateServerPayload) => {
    try {
      await httpClientInstance.put(API_ENDPOINTS.awsConnect.instances.update(id), payload)
      await get().fetchServers()
    } catch (error: any) {
      const message =
        typeof error.response?.data === "object" && error.response?.data?.detail
          ? error.response.data.detail
          : error.message || "Failed to update server"
      throw new Error(message)
    }
  },

  fetchServers: async () => {
    set({ isLoading: true, error: null })
    try {
      const data = await httpClientInstance.get<Server[]>(API_ENDPOINTS.awsConnect.instances.list)
      set({ servers: data, isLoading: false, serversLoaded: true })
    } catch (error: any) {
      set({ error: error.message || "Failed to fetch servers", isLoading: false })
      // serversLoaded stays false so the next mount will auto-retry
    }
  },

  startServer: async (id: number) => {
    try {
      await httpClientInstance.post(API_ENDPOINTS.awsConnect.instances.start(id))
      await get().fetchServers()
    } catch (error: any) {
      const message =
        typeof error.response?.data === "object" && error.response?.data?.detail
          ? error.response.data.detail
          : error.message || "Failed to start server"
      throw new Error(message)
    }
  },

  stopServer: async (id: number) => {
    try {
      await httpClientInstance.post(API_ENDPOINTS.awsConnect.instances.stop(id))
      await get().fetchServers()
    } catch (error: any) {
      const message =
        typeof error.response?.data === "object" && error.response?.data?.detail
          ? error.response.data.detail
          : error.message || "Failed to stop server"
      throw new Error(message)
    }
  },

  restartServer: async (id: number) => {
    try {
      await httpClientInstance.post(API_ENDPOINTS.awsConnect.instances.reboot(id))
      await get().fetchServers()
    } catch (error: any) {
      const message =
        typeof error.response?.data === "object" && error.response?.data?.detail
          ? error.response.data.detail
          : error.message || "Failed to restart server"
      throw new Error(message)
    }
  },

  terminateServer: async (id: number) => {
    try {
      await httpClientInstance.post(API_ENDPOINTS.awsConnect.instances.terminate(id))
      set({
        servers: get().servers.filter((server) => server.id !== id),
        selectedServers: new Set([...get().selectedServers].filter((serverId) => serverId !== id)),
      })
    } catch (error: any) {
      const message =
        typeof error.response?.data === "object" && error.response?.data?.detail
          ? error.response.data.detail
          : error.message || "Failed to terminate server"
      throw new Error(message)
    }
  },

  assignDepartment: async (instanceId: number, departmentId: number) => {
    const data = await httpClientInstance.post<{ id: number }>(API_ENDPOINTS.cloudAllocation.allocations.create, {
      resource: instanceId,
      department: departmentId,
      allocation_reason: "",
    })
    set({
      servers: get().servers.map((s) =>
        s.id === instanceId ? { ...s, department: departmentId, allocation_id: data.id } : s
      ),
    })
  },

  unassignDepartment: async (instanceId: number) => {
    const server = get().servers.find((s) => s.id === instanceId)
    if (!server?.allocation_id) throw new Error("Allocation ID not found for this server")
    await httpClientInstance.delete(API_ENDPOINTS.cloudAllocation.allocations.delete(server.allocation_id))
    set({
      servers: get().servers.map((s) =>
        s.id === instanceId ? { ...s, department: null, department_name: null, allocation_id: null } : s
      ),
    })
  },

  selectServer: (id: number) => {
    const newSelected = new Set(get().selectedServers)
    newSelected.add(id)
    set({ selectedServers: newSelected })
  },

  unselectServer: (id: number) => {
    const newSelected = new Set(get().selectedServers)
    newSelected.delete(id)
    set({ selectedServers: newSelected })
  },

  selectAllServers: () => {
    set({ selectedServers: new Set(get().servers.map((s) => s.id)) })
  },

  clearSelection: () => {
    set({ selectedServers: new Set() })
  },

  reset: () =>
    set({
      servers: [],
      serversLoaded: false,
      instanceTypes: [],
      regions: [],
      isLoadingRegions: false,
      serverDetail: null,
      isLoading: false,
      isLoadingInstanceTypes: false,
      isLoadingDetail: false,
      serverMetrics: null,
      isLoadingMetrics: false,
      metricsError: null,
      serverStatus: null,
      isLoadingStatus: false,
      statusError: null,
      downloadingRdpId: null,
      error: null,
      selectedServers: new Set(),
      dashboardMetrics: [],
      dashboardMetricsSetupRequired: false,
      isLoadingDashboardMetrics: false,
      dashboardMetricsError: null,
      dashboardMetricsFetchedAt: null,
    }),
}))

// Selectors
export const selectRunningServers = (state: ManageServerStore) => state.servers.filter((s) => s.status === "running")

export const selectStoppedServers = (state: ManageServerStore) => state.servers.filter((s) => s.status === "stopped")

export const selectServerStats = (state: ManageServerStore) => ({
  total: state.servers.length,
  running: state.servers.filter((s) => s.status === "running").length,
  stopped: state.servers.filter((s) => s.status === "stopped").length,
  pending: state.servers.filter((s) => s.status === "pending").length,
})
